import { useMemo, useState } from "react";
import * as FaIcons from "react-icons/fa";
import { canonicalSchoolName } from "../../constants/schoolNameGroups";
import { schoolKey } from "../exportToExcel";

let nextId = 1;
const newId = () => `g${nextId++}`;

// Saved groups (from the server) first, then automatic suggestions for any
// school not already grouped. A saved group is shown when at least one of its
// schools is in this export; its other schools are kept in `hidden` so saving
// doesn't drop them.
function buildInitialGroups(schools, savedGroups) {
  const present = new Map(schools.map((s) => [s.key, s]));
  const used = new Set();
  const groups = [];

  for (const g of savedGroups || []) {
    const all = (g.members || []).filter((k) => !used.has(k));
    const members = all.filter((k) => present.has(k));
    if (members.length === 0 || !g.name) continue;
    all.forEach((k) => used.add(k));
    groups.push({
      id: newId(),
      name: g.name,
      members,
      hidden: all.filter((k) => !present.has(k)),
      enabled: true,
      source: "saved",
    });
  }

  const auto = new Map();
  for (const s of schools) {
    if (used.has(s.key)) continue;
    const canon = canonicalSchoolName(s.name);
    const k = schoolKey(canon);
    if (!auto.has(k)) auto.set(k, { name: canon, members: [] });
    auto.get(k).members.push(s.key);
  }
  for (const g of auto.values()) {
    if (g.members.length < 2) continue;
    groups.push({ id: newId(), name: g.name, members: g.members, hidden: [], enabled: true, source: "suggested" });
  }
  return groups;
}

const SOURCE_BADGE = {
  saved: "bg-indigo-100 text-indigo-700",
  suggested: "bg-amber-100 text-amber-700",
  manual: "bg-green-100 text-green-700",
};

// onConfirm(mergeMap, groupsToSave | null) — groupsToSave is null when the
// user chose not to save.
export default function SchoolMergeModal({ schools, savedGroups, onCancel, onConfirm }) {
  const byKey = useMemo(() => new Map(schools.map((s) => [s.key, s])), [schools]);
  const [groups, setGroups] = useState(() => buildInitialGroups(schools, savedGroups));
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [newName, setNewName] = useState("");
  const [remember, setRemember] = useState(true);
  const [error, setError] = useState("");

  const groupOf = useMemo(() => {
    const m = new Map();
    groups.forEach((g) => g.members.forEach((k) => m.set(k, g)));
    return m;
  }, [groups]);

  const studentCount = (keys) => {
    const ids = new Set();
    keys.forEach((k) => (byKey.get(k)?.ids || []).forEach((id) => ids.add(id)));
    return ids.size;
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? schools.filter((s) => s.name.toLowerCase().includes(q) || s.city.toLowerCase().includes(q)) : schools;
  }, [schools, search]);

  const updateGroup = (id, patch) =>
    setGroups((prev) => prev.map((g) => (g.id === id ? { ...g, ...patch } : g)));

  // Removing members can leave a group with one school — it is then just a rename.
  const removeMember = (id, key) =>
    setGroups((prev) =>
      prev
        .map((g) => (g.id === id ? { ...g, members: g.members.filter((k) => k !== key) } : g))
        .filter((g) => g.members.length > 0 || g.hidden.length > 0)
    );

  const removeGroup = (id) => setGroups((prev) => prev.filter((g) => g.id !== id));

  const toggleSelected = (key) =>
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });

  // Selected schools leave whatever group they were in.
  const withoutSelected = (list) =>
    list
      .map((g) => ({ ...g, members: g.members.filter((k) => !selected.has(k)) }))
      .filter((g) => g.members.length > 0 || g.hidden.length > 0);

  const createGroup = () => {
    if (selected.size < 2) {
      setError("Select at least two schools to merge.");
      return;
    }
    const members = [...selected];
    const name = newName.trim() || byKey.get(members[0])?.name || "";
    setGroups((prev) => [
      { id: newId(), name, members, hidden: [], enabled: true, source: "manual" },
      ...withoutSelected(prev),
    ]);
    setSelected(new Set());
    setNewName("");
    setError("");
  };

  const addSelectedTo = (id) => {
    const members = [...selected];
    setGroups((prev) => {
      const target = prev.find((g) => g.id === id);
      const rest = withoutSelected(prev.filter((g) => g.id !== id));
      const merged = {
        ...target,
        members: [...target.members.filter((k) => !selected.has(k)), ...members],
        source: target.source === "suggested" ? "manual" : target.source,
      };
      return [merged, ...rest];
    });
    setSelected(new Set());
    setError("");
  };

  const handleConfirm = () => {
    const active = groups.filter((g) => g.enabled && g.members.length > 0);
    const blank = active.find((g) => !g.name.trim());
    if (blank) {
      setError("Every merge group needs a school name.");
      return;
    }
    const map = {};
    active.forEach((g) => g.members.forEach((k) => (map[k] = g.name.trim())));
    // Saved: every ticked group, plus saved groups only unticked for this
    // export (removing a saved group is done with its trash icon). A group
    // needs two or more schools to be worth saving.
    const toSave = groups
      .filter((g) => g.enabled || g.source === "saved")
      .map((g) => ({ name: g.name.trim(), members: [...g.members, ...g.hidden] }))
      .filter((g) => g.name && g.members.length > 1);
    onConfirm(map, remember ? toSave : null);
  };

  const enabledCount = groups.filter((g) => g.enabled).length;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black bg-opacity-60" onClick={onCancel} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col z-10">
        {/* Header */}
        <div className="px-6 pt-5 pb-4 border-b">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 className="text-lg font-bold text-gray-800">Merge Schools</h2>
              <p className="text-xs text-gray-500 mt-1">
                Schools merged here appear as one row in the Schools Ranking sheet of this
                export. Student records and school names in the system are not changed.
              </p>
            </div>
            <button type="button" onClick={onCancel} className="text-gray-400 hover:text-gray-700">
              <FaIcons.FaTimes />
            </button>
          </div>
          {error && <p className="mt-3 text-sm text-red-600">{error}</p>}
        </div>

        <div className="flex-1 overflow-hidden grid grid-cols-1 md:grid-cols-2 gap-0">
          {/* Merge groups */}
          <div className="flex flex-col overflow-hidden border-b md:border-b-0 md:border-r">
            <div className="px-5 py-3 flex items-center justify-between bg-gray-50 border-b">
              <span className="text-sm font-semibold text-gray-700">
                Merge groups <span className="text-gray-400 font-normal">({enabledCount} selected)</span>
              </span>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {groups.length === 0 && (
                <p className="text-sm text-gray-500 text-center py-8">
                  No merges suggested. Select schools on the right to create one.
                </p>
              )}
              {groups.map((g) => (
                <div
                  key={g.id}
                  className={`rounded-xl border p-3 ${g.enabled ? "border-gray-200 bg-white" : "border-gray-100 bg-gray-50 opacity-60"}`}
                >
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      className="w-4 h-4 accent-blue-700 shrink-0"
                      checked={g.enabled}
                      onChange={(e) => updateGroup(g.id, { enabled: e.target.checked })}
                      title="Include this merge"
                    />
                    <input
                      type="text"
                      value={g.name}
                      onChange={(e) => updateGroup(g.id, { name: e.target.value })}
                      placeholder="Merged school name"
                      className="flex-1 min-w-0 border border-gray-300 rounded-lg px-2 py-1 text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-blue-400"
                    />
                    <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${SOURCE_BADGE[g.source]}`}>
                      {g.source}
                    </span>
                    <button
                      type="button"
                      onClick={() => removeGroup(g.id)}
                      className="shrink-0 text-gray-400 hover:text-red-600"
                      title="Remove this merge"
                    >
                      <FaIcons.FaTrash className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <ul className="mt-2 space-y-1">
                    {g.members.map((k) => (
                      <li key={k} className="flex items-center justify-between gap-2 text-xs text-gray-600 bg-gray-50 rounded-md px-2 py-1">
                        <span className="truncate" title={byKey.get(k)?.name}>
                          {byKey.get(k)?.name}
                          <span className="text-gray-400"> · {byKey.get(k)?.ids.length}</span>
                        </span>
                        <button
                          type="button"
                          onClick={() => removeMember(g.id, k)}
                          className="text-gray-400 hover:text-red-600 shrink-0"
                          title="Remove from this merge"
                        >
                          <FaIcons.FaTimes className="w-3 h-3" />
                        </button>
                      </li>
                    ))}
                  </ul>
                  {g.hidden.length > 0 && (
                    <p className="mt-1 text-[11px] text-gray-400">
                      + {g.hidden.length} more saved school{g.hidden.length === 1 ? "" : "s"} not in this export
                    </p>
                  )}
                  <div className="mt-2 flex items-center justify-between text-xs">
                    <span className="text-gray-500">{studentCount(g.members)} students after merge</span>
                    {selected.size > 0 && (
                      <button
                        type="button"
                        onClick={() => addSelectedTo(g.id)}
                        className="text-blue-700 font-semibold hover:underline"
                      >
                        + Add {selected.size} selected
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* All schools — manual merging */}
          <div className="flex flex-col overflow-hidden">
            <div className="px-5 py-3 bg-gray-50 border-b space-y-2">
              <span className="text-sm font-semibold text-gray-700">
                All schools <span className="text-gray-400 font-normal">({schools.length})</span>
              </span>
              <div className="relative">
                <FaIcons.FaSearch className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-400" />
                <input
                  type="search"
                  placeholder="Search schools or cities..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg pl-8 pr-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                />
              </div>
            </div>
            <div className="flex-1 overflow-y-auto">
              {filtered.map((s) => {
                const g = groupOf.get(s.key);
                return (
                  <label
                    key={s.key}
                    className={`flex items-center gap-2 px-5 py-2 text-sm border-b border-gray-100 cursor-pointer hover:bg-blue-50 ${
                      selected.has(s.key) ? "bg-blue-50" : ""
                    }`}
                  >
                    <input
                      type="checkbox"
                      className="w-4 h-4 accent-blue-700 shrink-0"
                      checked={selected.has(s.key)}
                      onChange={() => toggleSelected(s.key)}
                    />
                    <span className="flex-1 min-w-0">
                      <span className="block truncate text-gray-800">{s.name}</span>
                      <span className="block text-[11px] text-gray-400 truncate">
                        {s.city || "—"} · {s.ids.length} student{s.ids.length === 1 ? "" : "s"}
                        {g && g.enabled && <span className="text-blue-600"> · in “{g.name}”</span>}
                      </span>
                    </span>
                  </label>
                );
              })}
              {filtered.length === 0 && (
                <p className="text-sm text-gray-500 text-center py-8">No schools match.</p>
              )}
            </div>
            <div className="px-5 py-3 border-t bg-gray-50 flex flex-wrap items-center gap-2">
              <input
                type="text"
                placeholder={selected.size ? "Merged name (optional)" : "Select schools above"}
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                disabled={selected.size === 0}
                className="flex-1 min-w-[160px] border border-gray-300 rounded-lg px-2 py-1.5 text-sm disabled:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-400"
              />
              <button
                type="button"
                onClick={createGroup}
                disabled={selected.size < 2}
                className="px-3 py-1.5 rounded-lg bg-blue-700 text-white text-sm font-semibold hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                Merge {selected.size > 1 ? `${selected.size} schools` : "selected"}
              </button>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t flex flex-wrap items-center justify-between gap-3">
          <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
            <input
              type="checkbox"
              className="accent-blue-700"
              checked={remember}
              onChange={(e) => setRemember(e.target.checked)}
            />
            Save these merges to the system (shared with all users)
          </label>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 rounded-lg bg-gray-100 text-gray-700 text-sm font-medium hover:bg-gray-200"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => onConfirm({}, null)}
              className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 text-sm font-medium hover:bg-gray-50"
            >
              Export without merging
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              className="px-4 py-2 rounded-lg bg-blue-700 text-white text-sm font-semibold hover:bg-blue-600"
            >
              Confirm Merge &amp; Export
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
